import { Quasar, type QuasarLanguage } from 'quasar'
import { type Locale, type FieldLocalizedMessage } from '@/admin/types/locale'
import { type i18nMessagesDTOV2 } from '@/common/types/i18n'

const langList = import.meta.glob<{ default: QuasarLanguage }>('/node_modules/quasar/lang/*.js')

export const getActiveLocales = (locales: Locale[]) => {
  return locales.filter((locale) => locale.active)
}

export const getDefaultLocale = (locales: Locale[]) => {
  return locales.find((locale) => locale.default)
}

export const sortLocalesByOrder = (locales: Locale[]) => {
  return locales.toSorted((a, b) => a.displayOrder - b.displayOrder)
}

export const getLocaleByCode = (locales: Locale[], localeCode: string) => {
  return locales.find((locale) => locale.code === localeCode)
}

export const localizedFieldsToI18nApiMessages = (translations: FieldLocalizedMessage[], key: string): i18nMessagesDTOV2[] => {
  return translations.map((f) => {
    return {
      locale: f.locale,
      key,
      message: f.message,
    }
  })
}

export const getFieldLocalizedMessage = (translationsNames: i18nMessagesDTOV2[], locales: Locale[]): FieldLocalizedMessage[] => {
  return locales.map((locale) => {
    return {
      name: 'name',
      locale: locale.code,
      message: translationsNames.find((f) => f.locale === locale.code)?.message ?? '',
      displayOrder: 1,
    }
  })
}

/**
 * To update quasar language dynamically : https://quasar.dev/options/quasar-language-packs#dynamical-ssr-
 * Check also https://quasar.dev/options/app-internationalization/#detecting-locale
 * Warning: If you use Quasar’s set method (Quasar.lang.set()), this will not be reflected by Quasar’s getLocale above.
 * The reason for this is that getLocale() will always return the users locale (based on browser settings).
 * The set() method refers to Quasars internal locale setting which is used to determine which language file to use.
 * If you would like to see which language has been set using set() you can use Quasar.lang.props.isoName.
 */
export const changeQuasarLanguage = async (locale: string) => {
  const languageWithCountryCode = locale.replace('_', '-')
  const languageFallback = languageWithCountryCode.substring(0, 2)
  try {
    for (const targetLocale of [languageWithCountryCode, languageFallback]) {
      const langModule = `/node_modules/quasar/lang/${targetLocale}.js`
      if (langModule in langList) {
        const lang = await langList[langModule]()
        Quasar.lang.set(lang.default)
        break
      }
    }
  } catch {
    console.warn("User language doesn't exist in Quasar. Loaded 'en-US' language by default")
  }
}

export const getBrowserLocal = (): string => {
  // get default navigator local matching the iso format : [lang]_[country]
  const localeCodePattern = new RegExp(/^[a-z]{2}-[A-Z]{2}$/)
  for (const language of navigator.languages) {
    if (localeCodePattern.exec(language)) {
      return language
    }
  }
  return navigator.language
}
